import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ScanLine, Check, AlertTriangle } from 'lucide-react';
import { code128Geometry } from '../../utils/code128';
import {
  hardwareApi, type DeviceOrderRow, type DeviceUnitRow, type DeviceModelRow, type DeviceBundleRow,
} from '../../services/hardwareApi';
import { toastService } from '../../services/toastService';
import StockPickerModal from './StockPickerModal';
import { inventoryApi } from '../../services/inventoryApi';
import { useDataVersion } from '../../services/dataChangeBus';
import ProvisioningStagePanel from './ProvisioningStagePanel';
import ReportProblemButton from './ReportProblemButton';
import {
  Disclosure, DisclosureBody, AllocationChip, StageChip, UnitHeader, EmptySlot, DeviceThumb,
} from './stageShell';
import { RubyLoader } from '../../components/RubyLoader';

/**
 * ALLOCATE STOCK, WHERE THE ORDER IS.
 *
 * Each device the order calls for, with its picture, what is in stock, and two
 * ways to pick: SCAN a serial, or browse the units and tick them. Scanning is
 * first because it is what actually happens in a warehouse — somebody is
 * holding the box.
 *
 * Allocation itself goes through the same `allocateOrder` the rest of the app
 * uses, with `preferUnitIds` reordering its FIFO walk. There is no second
 * allocation path: a unit that is not in stock, or is of a model this order
 * does not need, is ignored rather than forced in, so picking here can never
 * double-allocate a device.
 */

interface Props {
  order: DeviceOrderRow;
  units: DeviceUnitRow[];
  models: DeviceModelRow[];
  bundles: DeviceBundleRow[];
  onAllocated: () => void;
  /**
   * ONE DEVICE, AS THE SERVICE JUST RETURNED IT.
   *
   * Each allocated device now carries its own checklist on its own card, and a
   * tick comes back with the unit it changed. Without this the only way to
   * reflect a tick is `onAllocated`, which refetches the whole order and
   * remounts the rail — throwing the reader's place away on every tick.
   *
   * Optional, so a caller that has not been updated behaves exactly as before.
   */
  onUnitChanged?: (unit: DeviceUnitRow) => void;
}

const ALIVE = new Set([
  'allocated', 'processing', 'provisioning', 'configured', 'packing', 'shipped', 'delivered', 'deployed',
  'on_hold', 'backordered',
]);

/**
 * The tracking id as a real CODE128-B symbol, from `code128Geometry` — the SAME
 * encoder the Rollo label pipeline uses. A symbol here that disagreed with the
 * one printed on the box would be worse than none at all.
 */
const PickBarcode: React.FC<{ value: string }> = ({ value }) => {
  const { bars, modules } = React.useMemo(() => code128Geometry(value), [value]);
  if (!modules) return null;
  return (
    <span className="flex shrink-0 flex-col items-center gap-[3px]">
      <svg viewBox={`0 0 ${modules} 20`} width={124} height={20} preserveAspectRatio="none"
        role="img" aria-label={`Barcode ${value}`} style={{ display: 'block' }}>
        {bars.map((b, i) => <rect key={i} x={b.x} y={0} width={b.width} height={20} fill="#1C1917" />)}
      </svg>
      <span className="whitespace-nowrap font-mono text-[10px] font-semibold tracking-[0.08em] text-stone-800">
        {value}
      </span>
    </span>
  );
};

/**
 * ONE UNIT, ROW BY ROW.
 *
 * These sat on a single flex line — barcode, id, serial, status — inside a
 * narrow stage column, so the serial truncated to "2342432…" and the tracking
 * id wrapped mid-token. A serial you cannot read is the one field an operator
 * needs to match against the box in their hand.
 *
 * Stacked and labelled instead: the barcode with its number beneath, then a
 * labelled row per fact. `break-all` on the serial because it is a vendor
 * string of unbounded length — truncating it silently is what caused this.
 */
const UnitLine: React.FC<{
  unit?: DeviceUnitRow;
  unitId: string;
  status?: string;
  onRemove?: () => void;
}> = ({ unit, unitId, status, onRemove }) => (
  <div className="rounded-lg border border-stone-200 bg-white p-2.5">
    <div className="flex items-start gap-2.5">
      <Check size={13} className="mt-0.5 shrink-0 text-emerald-600" />
      {unit?.trackingId
        ? <PickBarcode value={unit.trackingId} />
        : <span className="break-all font-mono text-[11px] text-stone-500">{unitId}</span>}
      {onRemove && (
        <button
          onClick={onRemove}
          aria-label="Remove this device from the order"
          className="ml-auto shrink-0 text-stone-400 hover:text-[#DC143C]"
        >×</button>
      )}
    </div>
    <dl className="mt-2 space-y-1 border-t border-stone-100 pt-2">
      {unit?.serialNumber && (
        <div className="flex gap-2">
          <dt className="w-[52px] shrink-0 text-[9.5px] font-bold uppercase tracking-wide text-stone-400">Serial</dt>
          <dd className="break-all font-mono text-[11px] text-stone-700">{unit.serialNumber}</dd>
        </div>
      )}
      <div className="flex gap-2">
        <dt className="w-[52px] shrink-0 text-[9.5px] font-bold uppercase tracking-wide text-stone-400">Status</dt>
        <dd className="text-[11px] font-semibold capitalize text-stone-700">
          {(status || unit?.state || '—').replace(/_/g, ' ')}
        </dd>
      </div>
    </dl>
  </div>
);

/**
 * The device's name, without saying the manufacturer twice.
 *
 * Several catalogue rows already carry the maker in `modelName` ("Partner Tech
 * E5 Dual Screen POS"), so prefixing `manufacturer` unconditionally rendered
 * "Partner Tech Partner Tech E5 Dual Screen POS" on this panel.
 */
export const deviceTitle = (m?: { manufacturer?: string; modelName?: string }, fallback = ''): string => {
  const name = (m?.modelName || '').trim();
  const make = (m?.manufacturer || '').trim();
  if (!name) return make || fallback;
  if (!make || name.toLowerCase().startsWith(make.toLowerCase())) return name;
  return `${make} ${name}`;
};

const when = (ts?: number) =>
  ts ? new Date(ts).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '';

/**
 * ONE UNIT BEING TAKEN, DRAWN.
 *
 * FIFO is a decision nobody sees: a serial is chosen, the order says
 * "allocated", and which box left the shelf is something you go and look up.
 * The reel spins while the request is in flight and lands on the unit the
 * SERVER actually allocated — not on a guess made here, which could disagree
 * with what was really taken and would be worse than showing nothing.
 *
 * It spins over the serials genuinely in stock. A reel showing a serial we do
 * not hold would be a lie in the one place the pick is watched.
 */
const CELL = 34;
const REEL_CELLS = 26;

const ReelStyle: React.FC = () => (
  <style>{`@keyframes allocReel { from { transform: translateY(0) } to { transform: translateY(-${CELL * 4}px) } }`}</style>
);

const Reel: React.FC<{ pool: string[]; winner: string | null; short?: boolean }> = ({ pool, winner, short }) => {
  const cells = React.useMemo(() => {
    const safe = pool.length ? pool : ['—'];
    const list = [winner || safe[0]];
    for (let i = 1; i < REEL_CELLS; i++) list.push(safe[(i * 2 + 1) % safe.length]);
    return list;
  }, [pool, winner]);

  const [settled, setSettled] = useState(false);
  useEffect(() => {
    if (!winner) { setSettled(false); return; }
    const t = setTimeout(() => setSettled(true), 30);
    return () => clearTimeout(t);
  }, [winner]);

  if (short) {
    return (
      <div className="flex-1 min-w-[150px] max-w-[260px] h-[34px] rounded-[9px] grid place-items-center border"
        style={{ background: 'linear-gradient(180deg,#1c1c20,#111114)', borderColor: 'rgba(245,158,11,.5)' }}>
        <span className="font-mono text-[11px] font-bold tracking-[.08em] text-amber-400">— NONE IN STOCK —</span>
      </div>
    );
  }

  return (
    <div
      className={`relative flex-1 min-w-[150px] max-w-[260px] h-[34px] rounded-[9px] overflow-hidden border ${
        settled ? 'border-emerald-400/60 shadow-[0_0_0_3px_rgba(74,222,128,.16)]' : 'border-white/[.09]'}`}
      style={{ background: 'linear-gradient(180deg,#1c1c20,#111114)' }}
    >
      <div
        className="absolute inset-x-0 top-0"
        style={settled
          ? { transform: 'translateY(0)', transition: 'transform 1.4s cubic-bezier(.12,.72,.16,1)' }
          : { transform: `translateY(-${(REEL_CELLS - 1) * CELL}px)`, animation: 'allocReel .34s linear infinite' }}
      >
        {cells.map((c, i) => (
          <div key={i} className={`h-[34px] grid place-items-center font-mono text-[11.5px] ${
            i === 0 && settled ? 'text-white font-bold' : 'text-zinc-500'}`}>
            {c}
          </div>
        ))}
      </div>
      {/* the glass, so the strip reads as a wheel rather than a list */}
      <span aria-hidden className="absolute inset-0 pointer-events-none"
        style={{ background: 'linear-gradient(180deg,rgba(0,0,0,.55),transparent 34%,transparent 66%,rgba(0,0,0,.55))' }} />
    </div>
  );
};

const AllocateStagePanel: React.FC<Props> = ({
  order, units, models, bundles, onAllocated, onUnitChanged,
}) => {
  /**
   * Counts only — never the units themselves.
   *
   * This panel used to open with `listUnits({ state: 'in_stock', limit: 500 })`,
   * so it got slower with every device the business had ever received, to render
   * a list an operator scrolls past. `stockSummary` answers "how many" without
   * loading one unit row; the picker loads a single model's stock when opened.
   */
  const [onHand, setOnHand] = useState<Record<string, number>>({});
  /**
   * What THIS order has reserved of each quantity-tracked model, from the
   * ledger. Such a model has no unit rows, so counting units read a reserved
   * cash drawer as "0 of 1 allocated" for ever.
   */
  const [reservedForOrder, setReservedForOrder] = useState<Record<string, number>>({});
  const [confirmFill, setConfirmFill] = useState(false);
  /** Full rows for the units actually picked, so the panel can show what they are. */
  const [pickedRows, setPickedRows] = useState<Record<string, DeviceUnitRow>>({});
  const [browsing, setBrowsing] = useState<string | null>(null);
  /** The order's units after allocation, so the panel can name what it took. */
  const [allocatedRows, setAllocatedRows] = useState<DeviceUnitRow[]>([]);
  const [scanning, setScanning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openModel, setOpenModel] = useState<string | null>(null);
  const [picked, setPicked] = useState<Record<string, Set<string>>>({});
  const [scan, setScan] = useState('');
  const [scanError, setScanError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  /* what each model's reels are showing: null winners = still in flight */
  const [reels, setReels] = useState<Record<string, { pool: string[]; winners: (string | null)[] }>>({});
  const scanRef = useRef<HTMLInputElement | null>(null);
  const dataVersion = useDataVersion();

  /** What the order calls for, per model — bundles expanded as the allocator expands them. */
  const need = useMemo(() => {
    const counts = new Map<string, number>();
    for (const item of order.items || []) {
      const qty = (item as any).quantity || 1;
      if ((item as any).bundleId) {
        const b = bundles.find((x) => x.bundleId === (item as any).bundleId);
        for (const c of b?.components || []) counts.set(c.modelId, (counts.get(c.modelId) || 0) + (c.quantity || 1) * qty);
      } else if ((item as any).modelId) {
        const id = (item as any).modelId as string;
        counts.set(id, (counts.get(id) || 0) + qty);
      }
    }
    return counts;
  }, [order.items, bundles]);

  const modelIds = useMemo(() => Array.from(need.keys()), [need]);

  /**
   * HOW MANY ARE ON THE SHELF — counted the way each model actually tracks.
   *
   * The two modes do NOT share a counter, and conflating them is what made this
   * panel report "0 in stock" for a device with six on the shelf:
   *
   *  - QUANTITY models have a real counter, served by `/lifecycle/stock`.
   *  - SERIALIZED models have none. Their stock IS the number of unit rows in
   *    `in_stock`, so it is counted by asking `listUnits` for ONE row and
   *    reading `total` — the match count before pagination.
   *
   * Either way this loads at most one unit row per model, never the estate.
   */
  const load = useCallback(async () => {
    if (modelIds.length === 0) { setLoading(false); return; }
    const counts: Record<string, number> = {};

    const quantityIds = modelIds.filter((id) => (models.find((m) => m.modelId === id) as any)?.trackingMode === 'quantity');
    const serializedIds = modelIds.filter((id) => !quantityIds.includes(id));

    await Promise.all([
      (async () => {
        if (quantityIds.length === 0) return;
        try {
          // AVAILABLE, not on-hand: units reserved by other orders cannot be taken.
          const res = await inventoryApi.stockSummary(quantityIds);
          for (const [id, v] of Object.entries(res || {})) counts[id] = (v as any)?.available ?? (v as any)?.onHand ?? 0;
        } catch { /* a missing counter reads as unknown, handled as 0 below */ }
        const mine: Record<string, number> = {};
        await Promise.all(quantityIds.map(async (id) => {
          try {
            const { items } = await inventoryApi.stockMovements(id);
            let net = 0;
            for (const m of items || []) {
              if (m.orderId !== order.orderId) continue;
              if (m.kind === 'reserved') net += m.qty;
              else if (m.kind === 'released') net -= m.qty;
            }
            mine[id] = Math.max(0, net);
          } catch { /* unknown reads as nothing reserved */ }
        }));
        setReservedForOrder(mine);
      })(),
      ...serializedIds.map(async (id) => {
        try {
          const res = await hardwareApi.listUnits({ state: 'in_stock', modelId: id, limit: 1 });
          // `total` is new; a server without it still reports this page's size,
          // which for limit:1 is 0 or 1 — enough to know whether ANY exist.
          counts[id] = res.total ?? res.count ?? 0;
        } catch { /* leave it absent — rendered as 0 */ }
      }),
    ]);

    setOnHand(counts);
    setLoading(false);
  }, [modelIds.join(','), models, order.orderId, dataVersion]); // dataVersion: re-read after any save on the page

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => Array.from(need.entries()).map(([modelId, wanted]) => {
    const model = models.find((m) => m.modelId === modelId);
    const isQuantity = (model as any)?.trackingMode === 'quantity';
    const already = isQuantity
      ? Math.min(wanted, reservedForOrder[modelId] ?? 0)
      : units.filter((u) => u.modelId === modelId && ALIVE.has(u.state)).length;
    return {
      modelId,
      model,
      wanted,
      already,
      outstanding: Math.max(0, wanted - already),
      inStock: onHand[modelId] ?? 0,
      isQuantity,
    };
  }), [need, models, units, onHand, reservedForOrder]);

  /**
   * ONE DEVICE, SAVED NOW. A scan, a browse pick, or reserving a counted model
   * goes straight to the server for that model alone — nothing waits on a
   * second button, and no other model is filled from FIFO behind its back.
   */
  const allocateOne = async (modelId: string, unitIds: string[]) => {
    setSaving(true);
    try {
      const res = await hardwareApi.allocateOrder(order.orderId, unitIds, {
        onlyModelIds: [modelId],
        onlyPreferred: unitIds.length > 0,
      });
      const a = (res.allocations || []).find((x) => x.modelId === modelId);
      const name = deviceTitle(models.find((m) => m.modelId === modelId), modelId);
      if (a && a.allocatedCount > 0) {
        const ids = unitIds.map((id) => pickedRows[id]?.trackingId || pickedRows[id]?.serialNumber).filter(Boolean);
        toastService.success(ids.length ? `${ids.join(', ')} allocated.` : `${a.allocatedCount} × ${name} reserved.`);
      } else {
        toastService.warning(unitIds.length
          ? 'Not allocated — that unit is no longer in stock or this order already has enough of it.'
          : `Nothing reserved — no ${name} available.`);
      }
      setPicked((prev) => { const next = { ...prev }; delete next[modelId]; return next; });
      load();
      onAllocated();
    } catch (e: any) {
      toastService.error(e?.message || 'Could not allocate.');
    } finally {
      setSaving(false);
    }
  };

  const pickedFor = (modelId: string) => picked[modelId] || new Set<string>();

  const toggle = (modelId: string, unitId: string, outstanding: number) => {
    setPicked((prev) => {
      const next = new Set(prev[modelId] || []);
      if (next.has(unitId)) next.delete(unitId);
      else if (next.size >= outstanding) {
        toastService.info(`This order needs ${outstanding} of these. Untick one to swap.`);
        return prev;
      } else next.add(unitId);
      return { ...prev, [modelId]: next };
    });
  };

  /**
   * A scanned serial, resolved BY THE SERVER.
   *
   * This used to search an array of every in-stock unit the browser had been
   * made to download. One request now answers it, which is the whole reason the
   * panel no longer preloads. Scoped to THIS model so scanning the wrong box
   * says so rather than quietly allocating a device the order never asked for.
   */
  const onScan = async (modelId: string, outstanding: number) => {
    const code = scan.trim();
    if (!code) return;
    setScanning(true);
    setScanError(null);
    try {
      const res = await hardwareApi.listUnits({ serial: code, limit: 5 });
      const found = res.items || [];
      const hit = found.find((u) => u.modelId === modelId && u.state === 'in_stock');

      if (!hit) {
        const other = found[0];
        setScanError(
          !other
            ? `${code} is not a serial we hold.`
            : other.state !== 'in_stock'
              ? `${code} is not in stock — it is ${other.state.replace(/_/g, ' ')}.`
              : `${code} is a ${models.find((m) => m.modelId === other.modelId)?.modelName || other.modelId} — this order needs a different device here.`,
        );
        setScan('');
        return;
      }

      setPickedRows((prev) => ({ ...prev, [hit.unitId]: hit }));
      setScan('');
      if (outstanding <= 0) {
        toastService.info('This order already has all of these.');
        return;
      }
      await allocateOne(modelId, [hit.unitId]);
      scanRef.current?.focus();
    } catch (e: any) {
      setScanError(e?.message || 'Could not look that serial up.');
    } finally {
      setScanning(false);
    }
  };

  const allocate = async () => {
    const prefer = Object.values(picked).flatMap((set) => Array.from(set));
    const label = (u: DeviceUnitRow) => u.trackingId || u.serialNumber || u.unitId;

    /* start the reels on the models we are about to fill, spinning over the
       serials really in stock */
    const spinning: Record<string, { pool: string[]; winners: (string | null)[] }> = {};
    for (const r of rows) {
      if (r.outstanding === 0 || r.isQuantity) continue;
      // The reel spins over the serials we KNOW — the ones picked here. With no
      // preloaded estate-wide stock there is nothing else honest to spin over,
      // and inventing plausible serials would put ids on screen that name no
      // real device.
      const known = Array.from(pickedFor(r.modelId)).map((id) => {
        const u = pickedRows[id];
        return u ? label(u) : id;
      });
      spinning[r.modelId] = {
        pool: known.length ? known : ['· · ·'],
        winners: Array.from({ length: Math.min(r.outstanding, Math.max(1, known.length)) }, () => null),
      };
    }
    setReels(spinning);
    setSaving(true);
    try {
      const res = await hardwareApi.allocateOrder(order.orderId, prefer);

      /*
        LAND ON WHAT THE SERVER TOOK. `allocations[].allocatedUnitIds` is the
        record of what actually moved; resolving those ids back to serials here
        means the reel and the order can never disagree about which box left
        the shelf.
      */
      /**
       * Resolve what the server took into READABLE ids.
       *
       * `pickedRows` only holds units this operator picked by hand, so an
       * automatic allocation resolved nothing and the reel landed on raw
       * `unit_<uuid>` — an id that names nothing on any box. One scoped fetch
       * of this order's units gives every allocated unit its tracking id.
       */
      let resolved: DeviceUnitRow[] = Object.values(pickedRows);
      try {
        const fresh = await hardwareApi.listUnits({ orderId: order.orderId, limit: 200 });
        if (fresh.items?.length) resolved = fresh.items;
        setAllocatedRows(fresh.items || []);
      } catch { /* fall back to what was picked by hand */ }
      const byId = new Map(resolved.map((u) => [u.unitId, u]));
      const landed: Record<string, { pool: string[]; winners: (string | null)[] }> = {};
      for (const a of res.allocations || []) {
        const prev = spinning[a.modelId];
        if (!prev) continue;
        const won = (a.allocatedUnitIds || []).map((id) => {
          const u = byId.get(id);
          return u ? label(u) : id;
        });
        landed[a.modelId] = { pool: prev.pool, winners: won.length ? won : prev.winners.map(() => null) };
      }
      setReels((cur) => ({ ...cur, ...landed }));

      if (res.fullyAllocated) toastService.success('Allocated. The provisioning plans are now live for each device.');
      else {
        const short = (res.allocations || []).filter((a) => a.shortQty > 0)
          .map((a) => `${a.shortQty} × ${models.find((m) => m.modelId === a.modelId)?.modelName || a.modelId}`).join(', ');
        toastService.warning(short ? `Allocated what we had — still short: ${short}.` : 'Not fully allocated.');
      }
      setPicked({});
      /* let the reels finish landing before the panel reloads under them */
      // The parent reloads the order; this panel refreshes only its stock
    // counts. Running its own full reload alongside made the whole stage
    // visibly churn twice for one action.
    setTimeout(() => { onAllocated(); setReels({}); }, 1500);
    load();
    } catch (e: any) {
      setReels({});
      toastService.error(e?.message || 'Could not allocate.');
    } finally {
      setSaving(false);
    }
  };

  const totalPicked = Object.values(picked).reduce((n, s) => n + s.size, 0);

  if (loading) {
    return <RubyLoader variant="inline" label="reading stock…" />;
  }

  return (
    <div className="space-y-2">
      {rows.map((r) => {
        const open = openModel === r.modelId;
        const chosen = pickedFor(r.modelId);
        const done = r.outstanding === 0;

        /*
          THE DEVICES THIS MODEL ACTUALLY PUT ON THE ORDER.

          Unchanged in substance — this is the same set the flat barcode strip
          used to draw. What changed is that each one is now a card of its own,
          carrying its own checklist, instead of a line in a list with its work
          somewhere further down the page.
        */
        const mine = (allocatedRows.length ? allocatedRows : units)
          .filter((u) => u.modelId === r.modelId && ALIVE.has(u.state));

        return (
          <Disclosure
            key={r.modelId}
            /* The MODEL is the collapsible now, and it opens even when it is
               full: "Done" was the one state you could not look inside, which
               is exactly when somebody asks which boxes went. */
            open={open}
            onToggle={(next) => { setOpenModel(next ? r.modelId : null); setScanError(null); setScan(''); }}
            tone={done ? 'done' : 'plain'}
            headerClassName="bg-stone-50"
            header={(
              <span className="flex min-w-0 items-center gap-3">
                <DeviceThumb model={r.model} size={40} />
                <span className="min-w-0 flex-1">
                  {/* WRAPS, never truncates. In a lane this narrow "TCSNG …" is
                      not a device name — it is a shrug. */}
                  <span className="block break-words text-[12.5px] font-semibold leading-snug text-stone-900">
                    {deviceTitle(r.model, r.modelId)}
                  </span>
                  <span className="mt-0.5 block text-[11px] leading-snug text-stone-500">
                    {r.isQuantity
                      ? `tracked by quantity · ${r.inStock} available`
                      : `${r.inStock} in stock`}
                    {mine.length > 0 && ` · ${mine.length} on this order`}
                  </span>
                </span>
              </span>
            )}
            chip={(
              <span className="flex shrink-0 items-center gap-1.5">
                {r.inStock === 0 && !done && <StageChip tone="work">None in stock</StageChip>}
                {chosen.size > 0 && <StageChip tone="idle">{chosen.size} picked</StageChip>}
                <AllocationChip got={r.already} want={r.wanted} />
              </span>
            )}
          >
            <DisclosureBody className="space-y-2">
              {/*
                WHICH BOXES WENT, AND WHAT HAS BEEN DONE TO EACH.

                "Done" says the count was met and nothing about which physical
                devices left the shelf — the question an operator asks the
                moment something is wrong with the shipment. Same barcode the
                label carries, so it can be checked against the carton without
                opening another screen; and the device's own checklist for THIS
                stage is inside its card, drawn by the panel that already owns
                ticking, scoped to this one unit.
              */}
              {mine.map((u) => (
                <Disclosure
                  key={u.unitId}
                  tone="plain"
                  /* Beside the device, because that is where somebody is
                     standing when they find the screen cracked. */
                  actions={<ReportProblemButton units={[u]} onUnitChanged={onUnitChanged} subtle />}
                  header={(
                    <UnitHeader
                      unit={u}
                      model={r.model}
                      note={(
                        <>
                          Allocated
                          {u.state ? <> · <span className="capitalize">{String(u.state).replace(/_/g, ' ')}</span></> : null}
                        </>
                      )}
                    />
                  )}
                >
                  <DisclosureBody>
                    <ProvisioningStagePanel
                      units={[u]}
                      unitIds={[u.unitId]}
                      models={models}
                      stageIds={['allocated']}
                      bare
                      onUnitChanged={onUnitChanged}
                      onChanged={onAllocated}
                    />
                  </DisclosureBody>
                </Disclosure>
              ))}

              {/* The places still to fill, drawn rather than left as absence. */}
              {!r.isQuantity && Array.from({ length: r.outstanding }, (_, i) => (
                <EmptySlot
                  key={`slot-${i}`}
                  model={r.model}
                  text={`Device ${mine.length + i + 1} of ${r.wanted} — not allocated. Scan a serial or browse stock.`}
                />
              ))}

            {!done && (
              <div className="rounded-xl border border-stone-200 bg-white p-3">
                {r.isQuantity && (
                  <div className="flex flex-wrap items-center gap-3">
                    <p className="flex-1 min-w-[200px] text-[12px] text-stone-500">
                      {r.inStock > 0
                        ? `No serials to pick — reserves ${Math.min(r.outstanding, r.inStock)} from the ${r.inStock} available.`
                        : 'None available — receive stock on a GRN first.'}
                    </p>
                    <button
                      onClick={() => allocateOne(r.modelId, [])}
                      disabled={saving || r.inStock === 0}
                      className="inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-[#DC143C] px-4 text-[12px] font-semibold text-white hover:bg-[#9F1239] disabled:opacity-40"
                    >
                      {saving && <RubyGemMark size={13} drawing color="currentColor" />}
                      Reserve {Math.min(r.outstanding, Math.max(1, r.inStock))}
                    </button>
                  </div>
                )}

                {!r.isQuantity && (
                  <>
                    {/* SCAN FIRST — somebody is holding the box. */}
                    <div className="flex items-center gap-2 mb-2">
                      <ScanLine size={14} className="text-stone-400 shrink-0" />
                      <input
                        ref={scanRef}
                        value={scan}
                        autoFocus
                        onChange={(e) => { setScan(e.target.value); setScanError(null); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onScan(r.modelId, r.outstanding); } }}
                        placeholder="Scan or type a serial…"
                        className="flex-1 px-3 py-2 rounded-lg border border-stone-200 text-[12.5px] font-mono text-stone-800 focus:outline-none focus:border-rose-500 focus:ring-2 focus:ring-rose-500/10"
                      />
                    </div>
                    {scanError && (
                      <p className="text-[11.5px] text-amber-700 mb-2 flex items-start gap-1.5">
                        <AlertTriangle size={12} className="shrink-0 mt-0.5" /> {scanError}
                      </p>
                    )}

                    {r.inStock === 0 && (
                      <p className="text-[12px] text-stone-500">Nothing in stock for this model.</p>
                    )}

                    {/* …or browse. The list lives in a modal, paginated, so a
                        model with 400 units opens as fast as one with four. */}
                    <button
                      onClick={() => setBrowsing(r.modelId)}
                      disabled={r.inStock === 0}
                      className="min-h-[40px] w-full rounded-lg border border-stone-300 bg-white px-3 text-[12px] font-semibold text-stone-700 hover:border-[#DC143C] hover:text-[#DC143C] disabled:opacity-40"
                    >
                      Browse stock ({r.inStock})
                    </button>

                    {chosen.size > 0 && (
                      <div className="space-y-1 border-t border-dashed border-stone-300 pt-2">
                        {Array.from(chosen).map((unitId) => {
                          const u = pickedRows[unitId];
                          return (
                            <UnitLine
                              key={unitId}
                              unit={u}
                              unitId={unitId}
                              status="picked"
                              onRemove={() => toggle(r.modelId, unitId, r.outstanding)}
                            />
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            </DisclosureBody>
          </Disclosure>
        );
      })}

      {Object.keys(reels).length > 0 && (
        <div className="rounded-xl border border-stone-200 bg-white p-3 space-y-2.5">
          <ReelStyle />
          {rows.filter((r) => reels[r.modelId]).map((r) => (
            <div key={r.modelId}>
              <p className="text-[11px] text-stone-500 mb-1.5 break-words">
                {r.model ? `${r.model.manufacturer} ${r.model.modelName}` : r.modelId}
              </p>
              <div className="flex gap-2 flex-wrap">
                {reels[r.modelId].winners.map((w, i) => (
                  <Reel key={i} pool={reels[r.modelId].pool} winner={w} short={r.inStock === 0} />
                ))}
              </div>
            </div>
          ))}
          <p className="text-[11px] text-stone-400 leading-relaxed">
            Landing on the units the server actually took — not a guess made here.
          </p>
        </div>
      )}

      {rows.some((r) => r.outstanding > 0) && (
        <div className="flex items-center gap-3 flex-wrap pt-1">
          <button
            onClick={() => setConfirmFill(true)}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold min-h-[44px] disabled:opacity-50"
          >
            {saving && <RubyGemMark size={13} drawing color="currentColor" />}
            Fill the rest from oldest stock
          </button>
          <span className="text-[11.5px] text-stone-400">
            Scanning or picking a device saves it straight away. This fills whatever is still missing.
          </span>
        </div>
      )}

      {confirmFill && (() => {
        const missing = rows.filter((r) => r.outstanding > 0);
        return (
          <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 px-4" onClick={() => setConfirmFill(false)}>
            <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start gap-3">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-amber-100">
                  <AlertTriangle size={17} className="text-amber-700" />
                </span>
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-stone-900">Fill the rest automatically?</h3>
                  <p className="mt-1 text-[12.5px] leading-relaxed text-stone-600">
                    The system picks the <b>oldest units in stock</b> — not ones you choose — for:
                  </p>
                  <ul className="mt-2 space-y-0.5 text-[12.5px] text-stone-700">
                    {missing.map((r) => (
                      <li key={r.modelId}>• {r.outstanding} × {deviceTitle(r.model, r.modelId)}</li>
                    ))}
                  </ul>
                  <p className="mt-2 text-[12px] text-stone-500">Devices already allocated to this order stay as they are.</p>
                </div>
              </div>
              <div className="mt-5 flex justify-end gap-2">
                <button
                  onClick={() => setConfirmFill(false)}
                  className="min-h-[44px] rounded-lg border border-stone-300 px-4 text-[12.5px] font-semibold text-stone-700 hover:bg-stone-50"
                >Cancel</button>
                <button
                  onClick={() => { setConfirmFill(false); allocate(); }}
                  className="min-h-[44px] rounded-lg bg-[#DC143C] px-4 text-[12.5px] font-semibold text-white hover:bg-[#9F1239]"
                >Fill from oldest stock</button>
              </div>
            </div>
          </div>
        );
      })()}

      {rows.every((r) => r.outstanding === 0) && (
        <p className="text-[12px] text-emerald-700">Every device on this order is allocated.</p>
      )}

      {browsing && (() => {
        const r = rows.find((x) => x.modelId === browsing);
        if (!r) return null;
        return (
          <StockPickerModal
            modelId={browsing}
            model={r.model}
            outstanding={r.outstanding}
            initialSelected={Array.from(pickedFor(browsing))}
            onClose={() => setBrowsing(null)}
            onApply={(chosenUnits) => {
              // Replace this model's picks wholesale: the modal owned the
              // selection while it was open, so merging would resurrect units
              // the operator had just unticked in it.
              setPicked((prev) => ({ ...prev, [browsing]: new Set(chosenUnits.map((u) => u.unitId)) }));
              // Keep the ROWS too. Without this the panel had only ids and fell
              // back to rendering `unit_455dc8b8-…`, which names nothing an
              // operator can read off a box.
              setPickedRows((prev) => {
                const next = { ...prev };
                for (const u of chosenUnits) next[u.unitId] = u;
                return next;
              });
              const modelId = browsing;
              setBrowsing(null);
              // Picked in the browser = chosen, so save it now like a scan.
              if (chosenUnits.length) allocateOne(modelId, chosenUnits.map((u) => u.unitId));
            }}
          />
        );
      })()}
    </div>

  );
};

export default AllocateStagePanel;
